import * as React from 'react';
import { AlertCircle, Clock, Loader2 } from 'lucide-react';

import { Button } from '../components/button';
import { cn } from '../lib/utils';

// Feedback states. They carry no copy of their own: every caller passes its text from the app's copy catalogue.

interface StateProps {
  title: string;
  description?: string;
  className?: string;
}

/** Single line of muted text, no illustration (look and feel §12). */
export function EmptyState({ title, description, className }: StateProps) {
  return (
    <div data-slot="empty-state" className={cn('px-4 py-6 text-sm text-muted-foreground', className)}>
      <p className="font-semibold">{title}</p>
      {description && <p className="mt-1">{description}</p>}
    </div>
  );
}

/** Minimal spinner with an accessible label. */
export function LoadingState({ label, className }: { label: string; className?: string }) {
  return (
    <div
      data-slot="loading-state"
      role="status"
      aria-live="polite"
      className={cn('flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground', className)}
    >
      <Loader2 aria-hidden className="size-4 animate-spin" />
      <span>{label}</span>
    </div>
  );
}

/** A request failed. Shows the reason and an optional retry. */
export function ErrorState({
  title,
  description,
  retryLabel,
  onRetry,
  className,
}: StateProps & { retryLabel?: string; onRetry?: () => void }) {
  return (
    <div
      data-slot="error-state"
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-sm border border-[#fca5a5] bg-missed-tint px-4 py-3',
        className,
      )}
    >
      <AlertCircle aria-hidden className="mt-0.5 size-4 shrink-0 text-missed-text" />
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-bold text-brand-navy">{title}</p>
        {description && <p className="mt-0.5 text-muted-foreground">{description}</p>}
      </div>
      {onRetry && retryLabel && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * The capability is in the contract but not built yet (the API answers 501). A calm, neutral panel: not an error
 * the user caused, and nothing to retry.
 */
export function NotAvailableState({ title, description, className }: StateProps) {
  return (
    <div
      data-slot="not-available-state"
      role="status"
      className={cn('flex items-start gap-3 rounded-sm border border-border bg-chrome px-4 py-3', className)}
    >
      <Clock aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div className="text-sm">
        <p className="font-semibold text-brand-navy">{title}</p>
        {description && <p className="mt-0.5 text-muted-foreground">{description}</p>}
      </div>
    </div>
  );
}

/** Small uppercase divider label. Earned eyebrows only: real section dividers, never decoration. */
export function SectionLabel({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn('text-[0.72rem] font-bold tracking-[0.05em] text-muted-foreground uppercase', className)}
      {...props}
    />
  );
}
